import React, { createContext, useContext, useEffect, useState } from 'react';
import { Environment, LlmModel, VlmModel, VqaThumbnailAppearance } from '../types';

const getEnumValue = <T extends Record<string, string>>(enumObj: T, stored: string | null, defaultValue: T[keyof T]): T[keyof T] => {
  return stored && Object.values(enumObj).includes(stored as T[keyof T]) ? stored as T[keyof T] : defaultValue;
};

type Theme = 'dark' | 'light';

interface AppSettingsContextType {
  theme: Theme;
  setTheme: (theme: Theme) => void;
  toggleTheme: () => void;
  developerMode: boolean;
  setDeveloperMode: (enabled: boolean) => void;
  environment: Environment;
  setEnvironment: (env: Environment) => void;
  // VQA Settings
  vqaThumbnailAppearance: VqaThumbnailAppearance;
  setVqaThumbnailAppearance: (value: VqaThumbnailAppearance) => void;
  // Model Settings
  vlmModel: VlmModel;
  setVlmModel: (value: VlmModel) => void;
  llmModel: LlmModel;
  setLlmModel: (value: LlmModel) => void;
}

const AppSettingsContext = createContext<AppSettingsContextType | undefined>(undefined);

export function AppSettingsProvider({ children }: { children: React.ReactNode }) {
  const [theme, setTheme] = useState<Theme>(() => {
    if (typeof window !== 'undefined') {
      try {
        const stored = localStorage.getItem('theme');
        if (stored === 'dark' || stored === 'light') return stored;
      } catch {}
    }
    return 'light';
  });

  const [developerMode, setDeveloperModeState] = useState(() => {
    if (typeof window !== 'undefined') {
      try {
        return localStorage.getItem('developerMode') === 'true';
      } catch {}
    }
    return false;
  });

  const [environment, setEnvironmentState] = useState<Environment>(() => {
    if (typeof window !== 'undefined') {
      try {
        const stored = localStorage.getItem('environment');
        return getEnumValue(Environment, stored, Environment.PRODUCTION);
      } catch {}
    }
    return Environment.PRODUCTION;
  });

  const [vqaThumbnailAppearance, setVqaThumbnailAppearanceState] = useState<VqaThumbnailAppearance>(() => {
    if (typeof window !== 'undefined') {
      try {
        const stored = localStorage.getItem('vqa.thumbnail_appearance');
        return getEnumValue(VqaThumbnailAppearance, stored, VqaThumbnailAppearance.FIRST_FRAME);
      } catch {}
    }
    return VqaThumbnailAppearance.FIRST_FRAME;
  });

  const toggleTheme = () => {
    setTheme(prev => prev === 'dark' ? 'light' : 'dark');
  };

  const setDeveloperMode = (enabled: boolean) => {
    setDeveloperModeState(enabled);
    try {
      localStorage.setItem('developerMode', String(enabled));
    } catch {}
  };

  const setEnvironment = (env: Environment) => {
    setEnvironmentState(env);
    try {
      localStorage.setItem('environment', env);
    } catch {}
  };

  const setVqaThumbnailAppearance = (value: VqaThumbnailAppearance) => {
    setVqaThumbnailAppearanceState(value);
    try {
      localStorage.setItem('vqa.thumbnail_appearance', value);
    } catch {}
  };

  const [vlmModel, setVlmModelState] = useState<VlmModel>(() => {
    if (typeof window !== 'undefined') {
      try {
        const stored = localStorage.getItem('vqa.vlm_model');
        return getEnumValue(VlmModel, stored, VlmModel.CUSTOM_SVD_LONG);
      } catch {}
    }
    return VlmModel.CUSTOM_SVD_LONG;
  });

  const [llmModel, setLlmModelState] = useState<LlmModel>(() => {
    if (typeof window !== 'undefined') {
      try {
        const stored = localStorage.getItem('vqa.llm_model');
        return getEnumValue(LlmModel, stored, LlmModel.US_SONNET_4_6);
      } catch {}
    }
    return LlmModel.US_SONNET_4_6;
  });

  const setVlmModel = (value: VlmModel) => {
    setVlmModelState(value);
    try {
      localStorage.setItem('vqa.vlm_model', value);
    } catch {}
  };

  const setLlmModel = (value: LlmModel) => {
    setLlmModelState(value);
    try {
      localStorage.setItem('vqa.llm_model', value);
    } catch {}
  };

  useEffect(() => {
    const root = window.document.documentElement;
    root.classList.remove('light', 'dark');
    root.classList.add(theme);
    try {
      localStorage.setItem('theme', theme);
    } catch {}
  }, [theme]);

  return (
    <AppSettingsContext.Provider value={{ theme, setTheme, toggleTheme, developerMode, setDeveloperMode, environment, setEnvironment, vqaThumbnailAppearance, setVqaThumbnailAppearance, vlmModel, setVlmModel, llmModel, setLlmModel }}>
      {children}
    </AppSettingsContext.Provider>
  );
}

export const useAppSettings = () => {
  const context = useContext(AppSettingsContext);
  if (context === undefined) {
    throw new Error('useAppSettings must be used within an AppSettingsProvider');
  }
  return context;
};
